<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { required, email, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { type PropType } from "vue";
import { DensityType } from "@/app/common/types/form.type";
import { useI18n } from "vue-i18n";

const props = defineProps({
  modelValue: { type: Object, default: undefined },
  title: { type: String, default: "" },
  items: { type: Array<any> ,default:[]  }, 
  itemTitle:{ type: String, default: "title"},
  itemValue:{ type: String, default: "value" },
  itemProps: { type: String, default: "props" },
  returnObject:{ type: Boolean, default: false },
  multiple:{ type: Boolean, default: false },
  isRequired: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  hideDetails: { type: Boolean, default: false },
  showError: { type: Boolean, default: false },
  type: { type: String, default: "text" },
  isSubmitted: { type: Boolean, default: false },
  maxLength: { type: String, default: undefined },
  id: { type: String, default: undefined },
  isPassword: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  class: { type: String, default: "" },
  label: { type: String, default: "" },
  density: {
    type: String as PropType<DensityType>,
    default: "compact"
  }
});
const emit = defineEmits(["update:modelValue","onChange", "blur"]);
const {t} = useI18n()
const showPassword = ref(false);
const isValidation = computed(() => {
  return props.isRequired ;
});

const fieldValue = computed({
  get() {
    if (isValidation.value) {
      return (props.modelValue && props.modelValue.value) || "";
    } else {
      return props.modelValue || "";
    }
  },
  set(newValue: string) {
    if (isValidation.value) {
      emit("update:modelValue", {
        value: newValue,
        isValid: isValid.value
      });
    } else {
      emit("update:modelValue", newValue);
    }
  }
});

const rules = {
  fieldValue: {
    required: props.isRequired ? helpers.withMessage(t('t-please-fill') ,required) : {},
  }
};

const v$ = useVuelidate(rules, { fieldValue });

const onChange = () => {
  if (isValidation.value) {
    v$.value.$touch();
    emit("update:modelValue", {
      value: fieldValue.value,
      isValid: isValid.value
    });
  }
    emit('onChange',fieldValue.value)
};

const isSubmitted = computed(() => props.isSubmitted);
const isValid = computed(() => v$.value.fieldValue.$errors.length === 0);

const getType = computed(() => {
  if (props.isPassword) {
    if (!showPassword.value) {
      return "password";
    }
  }

  return props.type || "text";
});

watch(isSubmitted, () => {
  onChange();
});
</script>
<template>
  <div :class="class">
    <v-autocomplete
      v-model="fieldValue"
      :items="items"
      :item-title="itemTitle"
      :item-value="itemValue"
      :item-props="itemProps"
      :return-object="returnObject"
      :multiple="multiple"
      variant="solo"
      :density="density"
      :type="getType"
      class="text-field-component"
      :hide-details="hideDetails"
      :placeholder="placeholder"
      :maxLength="maxLength"
      :label="label"
      :disabled="disabled"
      :id="id"
      :class="{
        'is-invalid': isSubmitted && showError && !isValid,
        'is-valid': isSubmitted && showError && isValid
      }"
      @change="onChange"
      @blur="emit('blur', $event)"
    >
      <template #append-inner>
        <v-btn
          v-if="isPassword"
          density="compact"
          class="text-muted"
          :icon="showPassword ? 'ph-eye' : 'ph-eye-slash'"
          @click="showPassword = !showPassword"
        />
        <slot name="append-inner" />
      </template>
      <template #prepend-inner>
        <slot name="prepend-inner" />
      </template>
    </v-autocomplete>
  </div>
  <div
    v-if="isSubmitted && showError && v$.fieldValue.$invalid"
    class="invalid-feedback"
  >
    <span v-for="error in v$.fieldValue.$errors">
      {{ error.$message }}
    </span>
  </div>
  <div v-if="isSubmitted && showError && !v$.fieldValue.$invalid">
    <slot name="success-message" />
  </div>
</template>
